import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { AnalyticsPetInfo } from '@models/AnalyticsPetInfo';
import { InsuranceType } from '@models/InsuranceType';
import { Modality } from '@models/Modality';
import { Pet } from '@models/Pet';
import { TSFixme } from '@models/TSFixme';

interface AnalyticsWindow extends Window {
	dataLayer?: TSFixme[];
}

interface FORM_FIELD_PROPERTIES {
	'form_name': string;
	'field_name': string;
	'field_value'?: string | number;
	'field_type': string;
	'field_id': string;
	'action'?: string;
}

interface CLICK_PROPERTIES {
	'click_action'?: string;
	'click_type': string;
	'click_location': string;
	'click_text': string;
	'click_id': string;
}

interface ERROR_PROPERTIES {
	'error_type': string;
	'error_text': string;
	'error_id': string;
}

interface POPUP_PROPERTIES {
	'popup_id': string;
	'popup_action': string;
}

interface TOOLTIP_PROPERTIES {
	'tooltip_id': string;
}

interface FUNNEL_PROPERTIES {
	'step': string;
}

interface PURCHASE_PROPERTIES {
	'payment_modality': string;
	'premium_average': number;
	'premium_total': number;
	'premium_total_discounted': number;
	'total_discounts': number;
	'wellness_total': number;
	'value': number;
}

interface GTMDataLayer {
	/** The brand use to make the quote */
	'brand': string | number;
	/** The specific event that should be fired for the dataLayer analytics */
	'event': string;
	/** The page that the user is on */
	'pagePath': string;
	/** The pet's breed */
	'breed': string;
	/** The pet's species */
	'species': string;
	/** The pet's name */
	'petName': string;
	/** The pet parent's hashed SHA-256 email address */
	'emailAddressSha256': string;
	/** The pet parent's hashed SHA-128 email address */
	'emailAddressSha128': string;
	/** The pet parent's quote ID */
	'quoteId': string;
	/** The quote's current state, such as if the quote is new or retrieved */
	'quoteState': string;
	/** The pet parent's contact ID */
	'contactId': string;
	/** Whether the user has interacted with the Address 1 field on the Contact Information step */
	'addressInputInteraction': string;
	/** The pet parent's total premium on their purchased policy  */
	'totalPremium': number;
	/** The pet parent's policy number  */
	'policyNumber': string;
	/** The initial value of the Document Location for accurate referral tracking of quotes and policies */
	'originalLocation': string;
	/** The contact phone number associated with the branded or Embrace-version of the Quote Engine */
	'brandPhoneNumber': string;
	/** The formatted contact phone number associated with the branded or Embrace-version of the Quote Engine. */
	'brandFormattedPhoneNumber': string;
	/** The USA state that the pet parent created their quote from in the Quote Engine */
	'ratingState': string;
	/** The pet's index in the pet array */
	'petIndex': string | number;
	/** This is the first name of the pet parent */
	'firstName': string;
	/** This is the last name of the pet parent */
	'lastName': string;
	/** This is the zip or postal code that the pet parent created their quote from in the Quote Engine */
	'ratingZipCode': string;
	/** This is the pet parent phone number that the pet parent may have provided on step 1 / Pet Information in the Quote Engine */
	'phoneNumber': string;
	/** This is the pet parent's city that the pet parent has listed as their mailing address */
	'city': string;
	/** A query string (for Google Analytics virtual pageview) that may be added or updated as the user navigates the Quote Engine.*/
	'queryString': string;

	/** THE NEW GOOGLE ANALYTICS 4 ADDITIONS */
	/** The new checkout step for Google Analytics 4 */
	'checkoutStep': number;
	/** The new Google Analytics 4 value that shows what step the pet parent is on (pet information, pet added, policy edited, etc.) */
	'funnelStepName': 'Pet Information' | 'Select a Policy' | 'Contact Information' | 'Congratulations' | 'Pre Quote';
	/** The new Google Analytics 4 that shows what stage the pet parent is in the policy purchase process (start, reviewing, purchased) */
	'conversionName': 'Quote Start' | 'Quote Generated' | 'Quote Reviewed' | 'Policy Purchased' | 'Quote Redirected';
	/** The new Google Analytics 4 brand used to make the quote */
	'brandName': string;
	/** The new Google Analytics 4 pet parent's user identifier */
	'user_id': string;
	/** The new Google Analytics 4 value for pet parent's session type, such as a new or retrieved quote */
	'sessionType': string;
	/** The pet parent's optional answer on the Pet Information step. */
	'howDidYouHear': string;
	/** The new Google Analytics 4 value for the pet's species */
	'initialPetSpecies': string;
	/** The pet's sex (male or female) */
	'initialPetSex': string;
	/** The new Google Analytics 4 value for the pet's breed */
	'initialPetBreed': string;
	/** The pet's age in years */
	'initialPetAge': string;
	/** The military status of the pet parent */
	'military': 'y' | 'n';
	/** The payment frequency for the policy */
	'paymentFrequency': Modality;
	/** All pets have had a vet visit */
	'hadVetVisit': 'y' | 'n';
	/** The total number of pets on the quote */
	'numberOfPets': number;
	/** Pet-specific information for each pet on the quote */
	'pets': Array<{
		petId: string | undefined;
		wellnessSelected: string;
		accidentOnlySelected: string;
		annualReimbursementAmount: string;
		annualDeductibleAmount: string;
		reimbursementPercent: string;
		wellnessRewardAmount: string;
	}>;
	/** Total price of all pets */
	'value': number | undefined;
	/** Currency type for price of quote */
	'currency': 'USD';
	/** Specific pet info for ecommerce data */
	'items': AnalyticsPetInfo[];
	/** Ecommerce data for policy */
	'ecommerce': { 'items': GTMDataLayer['items'] };
	/** The policy id of the quote  */
	'transaction_id': string;
	/** The larger organization a brand is affilated with (always Embrace for now) */
	'affiliation': 'Embrace';
	/** Varation Name - Kameleoon variation key. EX: On, Off, or not_targeted*/
	'variation_name': string;
	/** Experiment Name - Kameleoon experiment feature key. EX: AB-11_Reduced_Coverage. Matches Kameleoon. */
	'experiment_name': string;
}

type DataLayerPayload = GTMDataLayer | CLICK_PROPERTIES
| FORM_FIELD_PROPERTIES | ERROR_PROPERTIES | POPUP_PROPERTIES
| FUNNEL_PROPERTIES | TOOLTIP_PROPERTIES | PURCHASE_PROPERTIES;

@Injectable({
	providedIn: 'root',
})
export class AnalyticsService {

	constructor(@Inject(DOCUMENT) private readonly document: HTMLDocument) { }

	public push(params: Partial<DataLayerPayload>): void {
		this.getWindow().dataLayer?.push(params);
	}

	public calculatePets(params: Pet[]): GTMDataLayer['pets'] {
		const petsOnQuote = params;
		const selectedPolicyOptions: Array<{
			petId: string | undefined;
			wellnessSelected: string;
			accidentOnlySelected: string;
			annualReimbursementAmount: string;
			annualDeductibleAmount: string;
			reimbursementPercent: string;
			wellnessRewardAmount: string;
		}> = [];

		petsOnQuote.forEach(pet => {
			const petSpecificCoverage = {
				petId: pet.petId,
				wellnessSelected: pet.wellnessPremium && pet.wellnessPremium > 0 ? 'y' : 'n',
				accidentOnlySelected: pet.insuranceType === InsuranceType.AccidentOnly ? 'y' : 'n',
				annualReimbursementAmount: pet.annualReimbursement?.find(({ rawValue }) =>
					rawValue === pet.annualReimbursementValue)?.viewValue ?? ``,
				annualDeductibleAmount: pet.annualDeductible?.find(({ rawValue }) =>
					rawValue === pet.annualDeductibleValue)?.viewValue ?? ``,
				reimbursementPercent: pet.reimbursement?.find(({ rawValue }) =>
					rawValue === pet.reimbursementValue)?.viewValue ?? ``,
				wellnessRewardAmount: pet.wellness?.find(({ rawValue }) =>
					rawValue === pet.wellnessValue)?.viewValue ?? ``,
			};
			selectedPolicyOptions.push(petSpecificCoverage);
		});

		return selectedPolicyOptions;
	}

	private getWindow(): AnalyticsWindow {
		// ! Check to make sure that this doesn't overwrite the window.dataLayer once it exists
		(this.document.defaultView as AnalyticsWindow).dataLayer ??= [];

		return this.document.defaultView as AnalyticsWindow;
	}
}
